import {useEffect, useRef, useState} from 'react';

interface FpsStats {
  currentFps: number;
  minFps: number;
  maxFps: number;
  averageFps: number;
}

const INITIAL_FPS_STATS: FpsStats = {
  currentFps: 0,
  minFps: Infinity,
  maxFps: 0,
  averageFps: 0,
};

export const useFps = (): FpsStats => {
  const [fpsStats, setFpsStats] = useState<FpsStats>(INITIAL_FPS_STATS);
  const frameCountRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(performance.now());
  const fpsHistoryRef = useRef<number[]>([]);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    const calculateFps = () => {
      const currentTime = performance.now();
      const deltaTime = currentTime - lastTimeRef.current;

      frameCountRef.current += 1;

      const ONE_SECOND_IN_MS = 1000;
      const HISTORY_LIMIT = 60;

      if (deltaTime >= ONE_SECOND_IN_MS) {
        const currentFps = Math.round((frameCountRef.current * ONE_SECOND_IN_MS) / deltaTime);

        fpsHistoryRef.current.push(currentFps);

        if (fpsHistoryRef.current.length > HISTORY_LIMIT) {
          fpsHistoryRef.current.shift();
        }

        const minFps = Math.min(...fpsHistoryRef.current);
        const maxFps = Math.max(...fpsHistoryRef.current);
        const averageFps = Math.round(
          fpsHistoryRef.current.reduce((sum, fps) => sum + fps, 0) / fpsHistoryRef.current.length
        );

        setFpsStats({
          currentFps: currentFps,
          minFps: minFps,
          maxFps: maxFps,
          averageFps: averageFps,
        });

        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;
      }

      animationFrameIdRef.current = requestAnimationFrame(calculateFps);
    };

    animationFrameIdRef.current = requestAnimationFrame(calculateFps);

    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, []);

  return fpsStats;
};
